// Ajuste em massa: um % ou um valor (R$) aplicado de uma vez nas contas de RECEITA ou de DESPESA,
// num período do plano (ano, trimestre ou mês).
//
// Contas do grupo: as de 1º nível do DRE (filhas das raízes totalizadoras, ou raízes comuns), pelo
// sinal natural da base — receita (+) ou despesa/dedução (−). Nas despesas, as contas que acompanham
// a receita ficam de fora por padrão (já variam com ela; somar um % aplicaria a variação duas vezes).
//
// Pré-preenchido (% sobre a base):
//   - %: cada categoria do grupo passa a ter o % no período (Substituir) ou ganha +% (Somar). Grava
//     na conta de 1º nível e em toda linha abaixo dela com % próprio (que prevaleceria sobre a conta).
//   - R$: vira o % equivalente sobre a base do grupo no período (R$ / |base|), igual em todas as contas.
//   As células sem base (R$) não mudam: % sobre zero não projeta nada.
// Zerado (valores em R$):
//   - R$: o total do grupo no período é dividido em partes iguais entre os meses e, em cada mês, entre
//     as contas conforme a base (Substituir) ou somado ao que já foi lançado (Somar).
//   - %: valor = base × (1 + %) (Substituir) ou valor atual × (1 + %) (Somar).

import { MONTH_INDEXES, zeros12 } from './periods';
import { isValueMode, nodeLinkedState, TARGET } from './adjustments';
import { addCategoriesValue, naturalSign, setCategoriesValue } from './valueMode';

export const BULK_GROUP = Object.freeze({ REVENUE: 'receitas', EXPENSE: 'despesas' });
export const BULK_KIND = Object.freeze({ PERCENT: 'percentual', VALUE: 'valor' });
export const BULK_MODE = Object.freeze({ REPLACE: 'substituir', ADD: 'somar' });

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/** Contas de 1º nível: filhas das raízes totalizadoras, ou as próprias raízes comuns. */
export const firstLevelNodeIds = (model) => {
  const ids = [];
  model.rootIds.forEach((rootId) => {
    const root = model.nodeById.get(rootId);
    if (root.isTotalizador) ids.push(...root.childIds);
    else ids.push(rootId);
  });
  return ids;
};

/** Contas de 1º nível do grupo (receita: sinal natural +; despesa: −). */
export const bulkTargetNodes = (model, state, group, { includeLinked = false } = {}) =>
  firstLevelNodeIds(model).filter((id) => {
    const node = model.nodeById.get(id);
    if (!node || node.isTotalizador || !node.allCats.length) return false;
    const revenue = naturalSign(model, TARGET.NODE, id) > 0;
    if (group === BULK_GROUP.REVENUE) return revenue;
    if (revenue) return false;
    // No zerado não há "acompanha a receita": todas as despesas entram.
    return includeLinked || isValueMode(state) || nodeLinkedState(model, state, id) !== true;
  });

const groupCategories = (model, nodeIds) => [...new Set(nodeIds.flatMap((id) => model.nodeById.get(id).allCats))];

/** % equivalente a somar `amount` (R$) à base das contas no período; null quando não há base. */
export const bulkEquivalentPct = (model, nodeIds, months, amount) => {
  let base = 0;
  groupCategories(model, nodeIds).forEach((cod) => {
    const series = model.base.get(cod) || [];
    months.forEach((m) => { base += series[m] || 0; });
  });
  return Math.abs(base) > 0.005 ? round2(((Number(amount) || 0) / Math.abs(base)) * 100) : null;
};

const updatePct = (state, targetType, key, months, fn) => {
  const bucket = targetType === TARGET.CATEGORY ? 'cats' : 'nodes';
  const series = state[bucket][key] ? [...state[bucket][key]] : zeros12();
  months.forEach((m) => { series[m] = round2(fn(series[m])); });
  const next = { ...state[bucket] };
  if (series.every((v) => !v)) delete next[key];
  else next[key] = series;
  return { ...state, [bucket]: next };
};

/** Linhas abaixo da conta com % próprio (nós descendentes e categorias da subárvore). */
const ownPctDescendants = (model, state, nodeId) => {
  const rows = [];
  const walk = (id) => {
    model.nodeById.get(id).childIds.forEach((childId) => {
      if (state.nodes[childId]) rows.push([TARGET.NODE, childId]);
      walk(childId);
    });
  };
  walk(nodeId);
  model.nodeById.get(nodeId).allCats.forEach((cod) => {
    if (state.cats[cod]) rows.push([TARGET.CATEGORY, cod]);
  });
  return rows;
};

const scaleValues = (model, state, cats, months, pct, add) => {
  const nextValues = { ...(state.values || {}) };
  cats.forEach((cod) => {
    const base = model.base.get(cod) || zeros12();
    const series = nextValues[cod] ? [...nextValues[cod]] : zeros12();
    months.forEach((m) => { series[m] = round2((add ? series[m] : base[m]) * (1 + pct / 100)); });
    if (series.every((v) => !v)) delete nextValues[cod];
    else nextValues[cod] = series;
  });
  return { ...state, values: nextValues };
};

/**
 * Aplica o ajuste em massa e devolve o novo estado (o mesmo estado quando não há o que aplicar).
 * `amount`: % (ex.: 10 = +10%) ou R$ (positivo = receita/despesa maior).
 */
export function applyBulk(model, state, { group, kind, amount, months = MONTH_INDEXES, mode = BULK_MODE.REPLACE, includeLinked = false }) {
  const value = Number(amount) || 0;
  const periodMonths = months && months.length ? months : MONTH_INDEXES;
  const targets = bulkTargetNodes(model, state, group, { includeLinked });
  if (!targets.length) return state;
  const add = mode === BULK_MODE.ADD;

  if (isValueMode(state)) {
    const cats = groupCategories(model, targets);
    if (kind === BULK_KIND.PERCENT) return scaleValues(model, state, cats, periodMonths, value, add);
    const signed = (group === BULK_GROUP.REVENUE ? 1 : -1) * value;
    return add
      ? addCategoriesValue(model, state, cats, periodMonths, signed)
      : setCategoriesValue(model, state, cats, periodMonths, signed);
  }

  const pct = kind === BULK_KIND.VALUE ? bulkEquivalentPct(model, targets, periodMonths, value) : value;
  if (pct == null) return state;
  const fn = add ? (current) => current + pct : () => pct;
  let next = state;
  targets.forEach((nodeId) => {
    next = updatePct(next, TARGET.NODE, nodeId, periodMonths, fn);
    ownPctDescendants(model, next, nodeId).forEach(([type, key]) => {
      next = updatePct(next, type, key, periodMonths, fn);
    });
  });
  return next;
}
